import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLocation } from "react-router-dom";
import { streamChat } from "../../services/marcoChat";
import { getSession } from "../../services/auth";
import arcspanIcon from "../../assets/images/arcspan-icon.png";
import "./index.scss";

// Pages where the chat widget should be hidden
const HIDDEN_PATHS = ["/login", "/password-recovery", "/404.html", "/ad-quality", "/arclens"];
const ALLOWED_EMAILS = ["jcabalugaz@arcspan.com", "bbledsoe@arcspan.com"];

function MarcoChatWidget() {
	const location = useLocation();
	const [isExpanded, setIsExpanded] = useState(false);
	const [messages, setMessages] = useState([]);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [hasUnread, setHasUnread] = useState(false);
	const messagesEndRef = useRef(null);
	const latestAssistantRef = useRef(null);
	const inputRef = useRef(null);
	const menuRef = useRef(null);
	const [lastScrolledAssistantId, setLastScrolledAssistantId] = useState(null);
	const [menuOpen, setMenuOpen] = useState(false);

	// Check if user is allowed (restricted to specific users)
	const session = getSession();
	const isAllowedUser = ALLOWED_EMAILS.includes(session?.email);
	const shouldHide = !isAllowedUser || HIDDEN_PATHS.includes(location.pathname);

	// Auto-scroll: to bottom for user messages, to start for new assistant messages
	useEffect(() => {
		if (!isExpanded) return;

		const lastMessage = messages[messages.length - 1];
		if (!lastMessage) return;

		if (lastMessage.role === "user") {
			// User message - scroll to bottom so they see it was sent
			messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
		} else if (lastMessage.role === "assistant" && lastMessage.id !== lastScrolledAssistantId) {
			// New assistant message - scroll to its start (once)
			setLastScrolledAssistantId(lastMessage.id);
			latestAssistantRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, [messages, isExpanded, lastScrolledAssistantId]);

	// Focus input and scroll to bottom when expanded
	useEffect(() => {
		if (isExpanded) {
			inputRef.current?.focus();
			// Scroll to bottom when re-opening
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
			}, 0);
		}
	}, [isExpanded]);

	// Handle ESC key to close
	useEffect(() => {
		const handleEsc = (e) => {
			if (e.key === "Escape" && isExpanded) {
				setIsExpanded(false);
				setMenuOpen(false);
			}
		};
		window.addEventListener("keydown", handleEsc);
		return () => window.removeEventListener("keydown", handleEsc);
	}, [isExpanded]);

	// Handle click outside to close menu
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setMenuOpen(false);
			}
		};
		if (menuOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [menuOpen]);

	// Hide widget on certain pages or if not allowed user
	// This must be after all hooks to avoid "Rendered more hooks" error
	if (shouldHide) {
		return null;
	}

	const handleSendMessage = async (e) => {
		e?.preventDefault();

		const trimmedMessage = inputValue.trim();
		if (!trimmedMessage || isLoading) return;

		// Add user message
		const userMessage = {
			id: Date.now(),
			role: "user",
			content: trimmedMessage,
			timestamp: new Date(),
		};

		setMessages((prev) => [...prev, userMessage]);
		setInputValue("");
		setIsLoading(true);

		// Create placeholder for assistant message
		const assistantMessageId = Date.now() + 1;
		setMessages((prev) => [
			...prev,
			{
				id: assistantMessageId,
				role: "assistant",
				content: "",
				timestamp: new Date(),
				isStreaming: true,
			},
		]);

		try {
			await streamChat({
				message: trimmedMessage,
				sessionId,
				onStart: (newSessionId) => {
					setSessionId(newSessionId);
				},
				onChunk: (content) => {
					setMessages((prev) =>
						prev.map((msg) =>
							msg.id === assistantMessageId
								? {
										...msg,
										content: msg.content
											? msg.content + "\n\n" + content
											: content,
								  }
								: msg
						)
					);
				},
				onComplete: (completedSessionId) => {
					setMessages((prev) =>
						prev.map((msg) =>
							msg.id === assistantMessageId
								? { ...msg, isStreaming: false }
								: msg
						)
					);
					setSessionId(completedSessionId);
					// Mark as unread if widget is collapsed
					if (!isExpanded) {
						setHasUnread(true);
					}
				},
				onError: (error) => {
					setMessages((prev) =>
						prev.map((msg) =>
							msg.id === assistantMessageId
								? {
										...msg,
										content: `Error: ${error.message}`,
										isStreaming: false,
										isError: true,
								  }
								: msg
						)
					);
				},
			});
		} catch (error) {
			console.error("Chat error:", error);
		} finally {
			setIsLoading(false);
			inputRef.current?.focus();
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const handleExpand = () => {
		setIsExpanded(true);
		setHasUnread(false);
	};

	const handleNewChat = () => {
		setMessages([]);
		setSessionId(null);
		setInputValue("");
		setMenuOpen(false);
		inputRef.current?.focus();
	};

	const formatTime = (date) => {
		return new Intl.DateTimeFormat("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		}).format(date);
	};

	const renderMessageContent = (message) => {
		if (!message.content) {
			return (
				<span className="typing-indicator">
					<span></span>
					<span></span>
					<span></span>
				</span>
			);
		}

		if (message.role === "assistant" && !message.isError) {
			return (
				<>
					<ReactMarkdown
						remarkPlugins={[remarkGfm]}
						components={{
							p: ({ children }) => <p>{children}</p>,
							strong: ({ children }) => <strong>{children}</strong>,
							ul: ({ children }) => <ul>{children}</ul>,
							ol: ({ children }) => <ol>{children}</ol>,
							li: ({ children }) => <li>{children}</li>,
							code: ({ inline, children }) =>
								inline ? (
									<code className="inline-code">{children}</code>
								) : (
									<pre className="code-block"><code>{children}</code></pre>
								),
							h1: ({ children }) => <h4>{children}</h4>,
							h2: ({ children }) => <h4>{children}</h4>,
							h3: ({ children }) => <h5>{children}</h5>,
							h4: ({ children }) => <h5>{children}</h5>,
							a: ({ href, children }) => (
								<a href={href} target="_blank" rel="noopener noreferrer">
									{children}
								</a>
							),
							table: ({ children }) => (
								<table className="markdown-table">{children}</table>
							),
							thead: ({ children }) => <thead>{children}</thead>,
							tbody: ({ children }) => <tbody>{children}</tbody>,
							tr: ({ children }) => <tr>{children}</tr>,
							th: ({ children }) => <th>{children}</th>,
							td: ({ children }) => <td>{children}</td>,
						}}
					>
						{message.content}
					</ReactMarkdown>
					{message.isStreaming && <span className="cursor">|</span>}
				</>
			);
		}

		return (
			<>
				{message.content}
				{message.isStreaming && <span className="cursor">|</span>}
			</>
		);
	};

	return (
		<div className="marco-chat-widget">
			{isExpanded ? (
				<div className="chat-panel">
					<div className="panel-header">
						<div className="header-info">
							<img src={arcspanIcon} alt="ArcSpan" className="header-icon" />
							<span className="header-title">Marco</span>
							<span className="beta-badge">Beta</span>
						</div>
						<div className="header-actions">
							<div className="menu-container" ref={menuRef}>
								<button
									className="menu-btn"
									onClick={() => setMenuOpen(!menuOpen)}
									title="Options"
								>
									⋮
								</button>
								{menuOpen && (
									<div className="dropdown-menu">
										<button
											onClick={handleNewChat}
											disabled={isLoading || messages.length === 0}
										>
											New Chat
										</button>
									</div>
								)}
							</div>
							<button
								className="close-btn"
								onClick={() => setIsExpanded(false)}
								title="Minimize"
							>
								−
							</button>
						</div>
					</div>

					<div className="messages-container">
						{messages.length === 0 ? (
							<div className="empty-state">
								<p>Hi! I&apos;m Marco, your AMS assistant.</p>
								<p>Ask me anything about your audiences, tags, or data.</p>
							</div>
						) : (
							<div className="messages-list">
								{messages.map((message, index) => (
									<div
										key={message.id}
										ref={message.role === "assistant" && index === messages.length - 1 ? latestAssistantRef : null}
										className={`message ${message.role} ${
											message.isError ? "error" : ""
										}`}
									>
										<div className="message-bubble">
											{renderMessageContent(message)}
										</div>
										<span className="timestamp">
											{formatTime(message.timestamp)}
										</span>
									</div>
								))}
								<div ref={messagesEndRef} />
							</div>
						)}
					</div>

					<div className="input-container">
						<div className="input-wrapper">
							<textarea
								ref={inputRef}
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Ask Marco..."
								disabled={isLoading}
								rows={1}
							/>
							<button
								type="button"
								onClick={handleSendMessage}
								disabled={!inputValue.trim() || isLoading}
								className="send-btn"
							>
								{isLoading ? "..." : "→"}
							</button>
						</div>
					</div>
				</div>
			) : (
				<button
					className={`chat-bar ${hasUnread ? "has-unread" : ""}`}
					onClick={handleExpand}
					title="Chat with Marco"
				>
					<img src={arcspanIcon} alt="ArcSpan" className="bar-icon" />
					<span className="bar-text">Ask Marco</span>
					<span className="beta-badge">Beta</span>
					{hasUnread && <span className="unread-badge"></span>}
				</button>
			)}
		</div>
	);
}

export default MarcoChatWidget;
